import { Link } from "react-router-dom";
import { percent } from "../../lib/format";
import type { FieldTrend, RunTrends as RunTrendsModel } from "./trends";
import { TREND_FIELD_CAP } from "./trends";

const SPARK_WIDTH = 132;
const SPARK_HEIGHT = 28;
/** Keeps the 2px stroke and the end dot inside the box at 0% and 100%. */
const SPARK_PAD = 4;

/**
 * One field's fill rate across the cached runs, oldest to newest.
 *
 * Plotted on a FIXED 0–100% scale rather than one fitted to the data: a
 * sparkline that rescales per field makes a two-point wobble look like a
 * collapse, and these lines sit in one column to be compared with each other.
 *
 * The SVG is decoration — `aria-hidden`, with the same numbers in the cells
 * beside it — so a screen reader or a forced-colors mode reads the row rather
 * than nothing at all.
 */
function Sparkline({ trend }: { trend: FieldTrend }) {
  const span = trend.points.length - 1;
  const usableWidth = SPARK_WIDTH - SPARK_PAD * 2;
  const usableHeight = SPARK_HEIGHT - SPARK_PAD * 2;
  const at = (index: number, rate: number) => ({
    x: SPARK_PAD + (span === 0 ? usableWidth : (index / span) * usableWidth),
    // Rate 1 belongs at the top, and SVG y grows downward.
    y: SPARK_PAD + (1 - Math.min(1, Math.max(0, rate))) * usableHeight
  });
  const points = trend.points.map((point, index) => ({ ...at(index, point.rate), point }));
  const path = points.map(({ x, y }, index) => `${index === 0 ? "M" : "L"}${x.toFixed(2)} ${y.toFixed(2)}`).join(" ");
  const end = points[points.length - 1]!;
  const direction = trend.last - trend.first;
  const endColor = direction < 0 ? "#dc2626" : direction > 0 ? "#059669" : "#475569";

  return (
    <svg
      aria-hidden="true"
      className="block"
      focusable="false"
      height={SPARK_HEIGHT}
      viewBox={`0 0 ${SPARK_WIDTH} ${SPARK_HEIGHT}`}
      width={SPARK_WIDTH}
    >
      <path d={path} fill="none" stroke="#475569" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} />
      {/* The newest run, so the eye lands on where the field is now. */}
      <circle cx={end.x} cy={end.y} fill={endColor} r={3} />
      {points.map(({ x, y, point }) => (
        // Hover detail without a tooltip layer: a wide invisible target per run.
        <circle cx={x} cy={y} fill="transparent" key={point.analysisKey} r={7}>
          <title>{`${point.ranAt.slice(0, 10)}: ${percent(point.rate)}`}</title>
        </circle>
      ))}
    </svg>
  );
}

/** Direction as a glyph plus a number, so it never reads by colour alone. */
function Change({ trend }: { trend: FieldTrend }) {
  const delta = trend.last - trend.first;
  const points = Math.abs(delta * 100).toFixed(1);
  if (delta === 0) {
    return <span className="text-slate-500">— unchanged</span>;
  }
  return (
    <span className={delta < 0 ? "text-red-700" : "text-emerald-700"}>
      {delta < 0 ? "▼" : "▲"} {points} pts
    </span>
  );
}

/**
 * How each field's fill rate has moved across the runs of this source that this
 * browser still holds.
 *
 * Context, never a verdict: the window is whatever survived cache eviction, and
 * a fill rate moves for legitimate reasons as much as for broken ones. The
 * panel says how many runs it is drawn from so the line is never read as a
 * complete history.
 */
export function RunTrends({ trends }: { trends: RunTrendsModel | null }) {
  if (trends === null || trends.runCount === 0) {
    return null;
  }

  if (trends.runCount < 2) {
    return (
      <section className="rounded-sm border bg-white p-4" data-testid="run-trends">
        <h3 className="font-medium">Fill rate across runs</h3>
        <p className="mt-2 text-sm text-slate-600">
          Trends appear once this source has been analysed more than once in this browser. This is the{" "}
          {trends.runCount === 1 ? "only run" : "first run"} of it held here.{" "}
          <Link className="underline" to="/runs">
            See the runs this browser holds
          </Link>
          .
        </p>
      </section>
    );
  }

  if (trends.trends.length === 0) {
    // Steady is a result, and one worth a sentence rather than a wall of flat
    // lines: it says the last few runs of this source carried the same shares.
    return (
      <section className="rounded-sm border bg-white p-4" data-testid="run-trends">
        <h3 className="font-medium">Fill rate across runs</h3>
        <p className="mt-2 text-sm text-slate-600" data-testid="run-trends-steady">
          No field moved its fill rate across the {trends.runCount} runs of this source still cached here — all{" "}
          {trends.comparedFieldCount} comparable fields carried the same share of values in each.
        </p>
      </section>
    );
  }

  return (
    <section className="rounded-sm border bg-white p-4" data-testid="run-trends">
      <h3 className="font-medium">Fill rate across runs</h3>
      <p className="mt-1 text-xs text-slate-500">
        {trends.truncated
          ? `The ${TREND_FIELD_CAP} fields that moved most`
          : `The ${trends.trends.length === 1 ? "one field" : `${trends.trends.length} fields`} that moved`}{" "}
        across the {trends.runCount} runs of this source still cached here, oldest to newest, on a fixed 0–100%
        scale.
        {trends.truncated
          ? ` ${trends.movedFieldCount - TREND_FIELD_CAP} more moved too — see Field Changes.`
          : ` The other ${trends.comparedFieldCount - trends.movedFieldCount} comparable fields held steady.`}{" "}
        The cache is bounded, so this is what is still held, not everything that ever ran.
      </p>
      <table className="mt-3 w-full text-sm" data-testid="run-trends-table">
        <thead>
          <tr className="text-left text-xs uppercase tracking-wide text-slate-500">
            <th className="pb-1 font-medium">Field</th>
            <th className="pb-1 font-medium">Trend</th>
            <th className="pb-1 font-medium">Oldest</th>
            <th className="pb-1 font-medium">Now</th>
            <th className="pb-1 font-medium">Change</th>
          </tr>
        </thead>
        <tbody>
          {trends.trends.map((trend) => (
            <tr className="border-t" data-testid={`run-trend:${trend.field}`} key={trend.field}>
              <th className="py-1 pr-3 text-left font-medium" scope="row">
                {trend.field}
              </th>
              <td className="py-1 pr-3">
                <Sparkline trend={trend} />
              </td>
              <td className="py-1 pr-3 tabular-nums text-slate-600">{percent(trend.first)}</td>
              <td className="py-1 pr-3 tabular-nums text-slate-600">{percent(trend.last)}</td>
              <td className="py-1 tabular-nums">
                <Change trend={trend} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
